import { useTranslation } from "react-i18next";
import { useAnalytics } from "~/components/analytics/AnalyticsProvider";
import { AppLink } from "~/components/ui";
import { ANALYTICS_EVENTS } from "~/lib/analytics/events";
import type { Language } from "~/lib/types";

/**
 * T976 — «El espejo»: the band under the hero that gives a woman her own sentence back
 * and takes her to its answer. Three rows, each one link: the sentence she would say
 * (serif, italic) and where it leads (a mono label). It replaced the hero's reading link
 * to the diastasis page (her decision, 2026-11-02: the link weakened the primary action)
 * and is paid for by the hero going back to its pre-T976 body.
 *
 * No cards, no icons, no buttons, no per-row background (her mock, `espejo-2294`,
 * 2026-11-02; the spec's Decisions quote it). Motion is the
 * public pair only: the label changes colour; nothing moves. Each click is one
 * `home_mirror_click` through the consent-gated provider, a no-op without consent.
 */
const ROWS = [
  { key: "diastasis", routeKey: "diastasis" },
  { key: "pelvic-floor", routeKey: "faq", hash: "#perdidas-orina" },
  { key: "method", routeKey: "metodo" },
] as const;

export function HomeMirror() {
  const { t, i18n } = useTranslation("home");
  const lang = (i18n.language ?? "es") as Language;
  const { capture } = useAnalytics();

  return (
    <section className="ho-mirror" id="espejo" aria-labelledby="ho-mirror-title">
      <div className="pub-wrap ho-mirror-grid">
        <h2 id="ho-mirror-title" className="pub-eyebrow ho-mirror-title">
          {t("mirror.title")}
        </h2>
        <div>
          <ul className="ho-mirror-list">
            {ROWS.map((row, index) => (
              <li key={row.key}>
                <AppLink
                  routeKey={row.routeKey}
                  hash={"hash" in row ? row.hash : undefined}
                  className="ho-mirror-row"
                  onClick={() => capture(ANALYTICS_EVENTS.homeMirrorClicked, { row: row.key, locale: lang })}
                >
                  <span className="ho-mirror-quote">{t(`mirror.rows.${index}.quote`)}</span>
                  <span className="ho-mirror-label">
                    {t(`mirror.rows.${index}.label`)}
                    <span aria-hidden="true">→</span>
                  </span>
                </AppLink>
              </li>
            ))}
          </ul>
          <p className="ho-mirror-foot">{t("mirror.foot")}</p>
        </div>
      </div>
    </section>
  );
}
